/**
 * Media Controller - controls the video element.
 */
'use strict';

import EventEmitter from 'eventemitter2';

//import Event from './HlsEvents';
import EventHandlers from './EventHandlers';
import Event from './HlsEvents';
import Utils from './HlsUtils';
import { logger } from './hlsjs/utils/logger';

//const HAVE_NOTHING = 0;
const HAVE_METADATA = 1;
//const HAVE_CURRENT_DATA = 2;
//const HAVE_FUTURE_DATA = 3;
//const HAVE_ENOUGH_DATA = 4;

/*
	IE will error unless the currentTime is set after HAVE_METADATA, Safari seems to silently fail, all other browsers don't care
	Probably smarter to just apply the rule to all
 */
const minReadyStateForCurrentTime = HAVE_METADATA;

/**
* @access internal
*/
class MediaController {
//Creation
	constructor(config = {}, di = {} ) {
		let disableWorkarounds = !!config.disableWorkarounds || false;

		this._emitter = di.eventEmitter || new EventEmitter();

		// source config properties
		this._media = null;
		this._subtitlesEnabled = false;
		this._seeking = false;
		// default properties used if no media attached, and no overrides.
		this._mediaDefaults = {
			muted: false,
			volume: 1.0,
			ended: false,
			duration: 0,
			readyState: 0,
			currentTime: 0,
			playbackRate: 1.0,
		};

		this._frameRate = 0;
		this._lastDecoded = 0;
		this._lastFrameRateTime = new Date();

		//ignore frame rate dropping to 0 at regular intervals
		this._safariFrameRateWorkaround = Utils.isSafari() && !disableWorkarounds;
		this._lastFrameRate = 0;

		//We need 2 Tizen seek workarounds here, as one is used for both Tizen 2 + 3 and the other is just Tizen 2
		//The first workaround is for both 2 + 3, and stops attempts to set current time to 0 when it hasn't been prompted to
		//The second workaround is 2 only, and re-seeks if the previous seek failed to get the right time
		this._tizenSeekWorkaround = Utils.isTizen() && !disableWorkarounds;
		this._tizen2ReseekWorkaround = Utils.isTizen2() && !disableWorkarounds;
		this._targetSeekTime = 0;

		//properly apply playbackRate at the start of a video
		this._usePendingPlaybackRate = true; //Utils.isTizen() && !disableWorkarounds; //needed to make the config setting work as designed
		this._pendingPlaybackRate = undefined;
		this._pendingCurrentTime = undefined; //For seeking before a media is attached

		//reduce amount of media element errors on seek
		//turned this off for now, as we're going to try and just let error recovery handle it
		//this._windows8Point1IESeekWorkaround = Utils.isIEWin8Point1() && !disableWorkarounds;
		this._windows8Point1IESeekWorkaround = false;
		this._pauseAfterSeek = false;

		//stop attempts to set playbackRate to 1 at the end of a seek
		this._IEPlaybackRateWorkaround = Utils.isIE() && !disableWorkarounds;
		this._rateChangeExpected = false;
		this._expectedPlaybackRate = 1;

		this._mediaOverrides = {
			// ie a bunch of undefined.
		};

		this._seekTimer = undefined;

		// media property values, that persist while media is not connected.
		this._handlers = new EventHandlers();

		// all the media events we will subscribe to
		this._mediaEvents = [
			{ event: 'seeking', 							func: this._onMediaSeeking 				},
			{ event: 'seeked',								func: this._onMediaSeeked 				},
			{ event: 'ended',								func: this._onMediaEnded 				},
			{ event: 'timeupdate',							func: this._onMediaTimeUpdate			},
			{ event: 'pause',								func: this._onMediaPause				},
			{ event: 'play',								func: this._onMediaPlay					},
			{ event: 'durationchange',						func: this._onMediaDurationChange		},
			{ event: 'playing',								func: this._onMediaPlaying				},
			{ event: 'loadedmetadata',						func: this._onMediaLoadedMetaData		}, //readyState 1 HAVE_METADATA
			{ event: 'loadeddata',							func: this._onMediaLoadedData			}, //readyState 2 HAVE_CURRENT_DATA
			{ event: 'canplay',								func: this._onMediaCanPlay				}, //readyState 3 HAVE_FUTURE_DATA
			{ event: 'canplaythrough',						func: this._onMediaCanPlayThrough		}, //readyState 4 HAVE_ENOUGH_DATA
			{ event: 'ratechange',							func: this._onMediaRateChange			},
			{ event: 'volumechange',						func: this._onMediaVolumeChange			},
			{ event: 'error',								func: this._onMediaError				},
		];
		EventHandlers.verifyEventsList(this._mediaEvents);

		Utils.applyConfig(this, config);
	}

	dispose() {
		if(this._seekTimer) {
			clearTimeout(this._seekTimer);
			this._seekTimer = undefined;
		}
		// triggers unwiring

		this._subtitlesEnabled = false;
		this.captionsControl();

		this.media = null;

		// this will destroy our event handlers too...
		this._handlers.dispose();
		this._handlers = null;

		this._mediaEvents = null;
		this._mediaDefaults = null;
		this._mediaOverrides = null;

		this._emitter = null;

		clearInterval(this._fpsMonitor);
		this._frameRate = 0;
		this._lastDecoded = 0;

		// need to destroy the EventEmitter _ee too.

		// ITS NOW ILLEGAL TO USE MediaController.
	}

	/**
	 * Wires a single media event
	 *
	 * @param {string} event - media element event type
	 * @param {function} func - event handler, must === the one in unwired.
	 */
	_wireMediaEvent({ event, func }) {
		let handler = this._handlers.createEventHandler(this, func);

		this._media.addEventListener(event, handler);
	}

	/**
	 * Unwires a single media event
	 *
	 * @param {string} event - media element event type
	 * @param {function} func - event handler, must === the one in wired.
	 */
	_unwireMediaEvent({ event, func }) {
		let handler = this._handlers.getHandlerForFunction(func);

		if (handler) {
			this._media.removeEventListener(event, handler);
		}
	}

	/**
	 * Wires all media events that we listen for.
	 *
	 * @emits MEDIA_CONTROLLER_WIRED {media}
	 */
	_wireMediaEvents() {
		let data = { media: this._media };
		logger.log( `MediaController::_wireMediaEvents: { media: ${data.media} }`);

		try {
			this._mediaEvents.forEach(event => this._wireMediaEvent(event));
			this._emit( Event.MEDIA_CONTROLLER_WIRED, data );
		}
		catch(e) {
			this._media = null; // otherwise we'll blow up when we unwire.
			this._emitMediaError(e);
		}
	}

	/**
	 * Unwires all registered media events.
	 *
	 * @emits MEDIA_CONTROLLER_UNWIRED {media}
	 */
	_unwireMediaEvents() {
		let data = { media: this._media };
		logger.log( `MediaController::_unwireMediaEvents: { media: ${data.media} }`);

		this._mediaEvents.forEach(event => this._unwireMediaEvent(event));

		this._emit( Event.MEDIA_CONTROLLER_UNWIRED, data );
	}

	/**
	 * Constructs and emits a MEDIA_CONTROLLER_ERROR
	 *
	 * @emits MEDIA_CONTROLLER_ERROR {details, [errorObj]}
	 *
	 * @todo add more error types etc... ie wiringError.
	 *
	 * @param {Error} errObj - the error obj, if there is one
	 */
	_emitMediaError(errObj) {
		let theError = {};
		theError.details = 'mediaControllerError';
		theError.errorObj = errObj || new Error( `MediaController::_emitMediaError '${theError.details}'`);

		this._emit(Event.MEDIA_CONTROLLER_ERROR, theError);
	}

	// MEDIA EVENTS

	_onMediaLoadedMetaData() {
		logger.log('MEDIA_LOADEDMETADATA'); //readyState 1 HAVE_METADATA

		this._mediaReadyStateChange();
	}

	_onMediaLoadedData() {
		logger.log('MEDIA_LOADEDDATA :' + this.currentTime); //readyState 2 HAVE_CURRENT_DATA

		this._emit(Event.MEDIA_LOADEDDATA);
	}

	_onMediaCanPlay() {
		logger.log('Media: CANPLAY : duration: ' + this.duration); //readyState 3 HAVE_FUTURE_DATA

		this._emit(Event.MEDIA_CANPLAY);
	}

	_onMediaCanPlayThrough() {
		logger.log('Media: CANPLAYTHROUGH : duration: ' + this.duration); //readyState 4 HAVE_ENOUGH_DATA

		this._emit(Event.MEDIA_CANPLAYTHROUGH);

	//	this.play();
	}

	_mediaReadyStateChange() {
		if (this._pendingCurrentTime && this._readyForCurrentTime()) { //note that media is checked in _readyForCurrentTime
			this._media.currentTime = this._pendingCurrentTime;
			this._pendingCurrentTime = undefined;
		}
	}


	_onMediaSeeked() {
		logger.log(`seeked: currentTime: ${this.currentTime}, _seeking: ${this._seeking}`);
		if(this._seeking) {

			//if( !this._seeking ) {
				// need to force the seeking event, since it was skipped by the media
				//this._onMediaSeeking();
			//}

			if(this._tizen2ReseekWorkaround && this.currentTime < this._targetSeekTime && this._targetSeekTime !== 0) {
				this.currentTime = this._targetSeekTime;
			}
			else {
				this._seeking = false;
				this._emit(Event.MEDIA_SEEKED, { currentTime : this.currentTime });

				if(this._pauseAfterSeek) {
					this.pause();
					this._pauseAfterSeek = false;
				}
			}
		}
	}

	// this is NOT always fired! Is mediaSeeked always fired?
	_onMediaSeeking() {
		logger.log(`seeking: currentTime: ${this.currentTime}, _seeking: ${this._seeking}`);
		if(!this._seeking) {

			/*if( this._seeking ) {
				//throw new Error( 'should not be seeking already');
			}*/

			this._seeking = true;

			if(this._tizenSeekWorkaround) {
				this._targetSeekTime = this.currentTime;
			}

			this._emit(Event.MEDIA_SEEKING);
		}
	}

	_onMediaEnded() {
		logger.log('ended :' + this.currentTime);

		this._targetSeekTime = 0;

		this._emit(Event.MEDIA_ENDED);
	}

	_onMediaTimeUpdate() {
		//logger.log('timeupdate :' + this.currentTime);
		if(this._tizenSeekWorkaround && this.currentTime === 0 && this._targetSeekTime !== 0) {
			this.currentTime = this._targetSeekTime;
		}
		else {
			this._emit(Event.MEDIA_TIMEUPDATE, { currentTime : this.currentTime } );
		}

		//Would the pendingPlaybackRate be better in one of the readyState checks? - SIEG
		if(this._pendingPlaybackRate !== undefined && this.currentTime !== 0) {
			let pendingPlaybackRate = this._pendingPlaybackRate;
			this._pendingPlaybackRate = undefined;
			this.playbackRate = pendingPlaybackRate;

			logger.log('_onMediaTimeUpdate: applying pending playback rate:' + pendingPlaybackRate);
		}
	}

	_onMediaPause() {
		logger.log('pause :' + this.currentTime);

		this._emit(Event.MEDIA_PAUSE);
	}

	_onMediaPlay() {
		logger.log('play :' + this.currentTime);

		this._emit(Event.MEDIA_PLAY);
	}

	_onMediaDurationChange() {
		logger.log('durationchange :' + this.duration);

		this._emit(Event.MEDIA_DURATIONCHANGE);
	}

	_onMediaPlaying() {
		logger.log('MEDIA_PLAYING playing :' + this.currentTime);

		//this._emit( Event.STREAM_PLAYING);
	}

	_onMediaRateChange() {
		//the timing of the playback rate reset in IE is late enough that we can't rely on being in the seeking state when it happens
		//as such we need to track whether or not we were expecting the playback rate to change
		if(this._IEPlaybackRateWorkaround && !this._rateChangeExpected && this.playbackRate === 1) {
			logger.log('_onMediaRateChange: rate change correction');
			this.playbackRate = this._expectedPlaybackRate;
		}
		else {
			this._rateChangeExpected = false;
			logger.log('_onMediaRateChange: ratechange: ' + this.playbackRate);

			this._emit(Event.MEDIA_RATECHANGE);
		}
	}

	_onMediaVolumeChange() {
		logger.log('volumechange :' + this.currentTime);

		this._emit(Event.MEDIA_VOLUMECHANGE);
	}

	_onMediaError() {
		// TODO: MediaError object is HTML5, and thus we can map more detail into the sub-error.
		let theError = {};
		theError.details = 'mediaElementError';
		theError.mediaErrorObj = this._media.error;

		if(theError.mediaErrorObj && theError.mediaErrorObj.msExtendedCode !== undefined) {
			theError.mediaErrorObj.msExtendedHex = Utils.msErrToHex(theError.mediaErrorObj.msExtendedCode);
		}
		theError.errorObj = new Error( `MediaController::_onMediaError '${theError.details}'`);
		logger.log('media error');

		this._emit(Event.MEDIA_ELEMENT_ERROR, theError);
	}

	get emitter() {
		return this._emitter;
	}

	_emit(event, ...args) {
		this._emitter.emit(event, ...args);
	}

	pause() {
		if(this._media) {
			this._media.pause();
		}
	}

	// returns a promise
	play() {
		let playPromise = this._media.play();

		if( playPromise === undefined ) {
			playPromise = Promise.resolve();
		}

		if(this._pauseAfterSeek) {
			//If we get a play command while pauseAfterSeek is on, assume that the user or player wants to play after seek and turn off pauseAfterSeek
			this._pauseAfterSeek = false;
		}

		return playPromise;
	}

	seek(seconds) {
		if(this._windows8Point1IESeekWorkaround && this.paused) {
			//IE11 on Win8.1 can crash on seek if paused. Work around by switching to playing state during the seek.
			this.play();
			this._pauseAfterSeek = true;
		}
		// should maybe clamp to sane values...
		this.currentTime = seconds;
	}

	get media() {
		return this._media;
	}

	set media(video) {
		if (this._media) {
			this._mediaOverrides = this._readMediaProperties();
			this._unwireMediaEvents();
			clearInterval(this._fpsMonitor);
		}

		this._media = video;

		if (this._media) {
			if( this._media.src) {
				// its not as simple as setting the src to null.
				// https://github.com/w3c/media-source/issues/53

				this._media.removeAttribute('src');
				this._media.load();
			}

			this._wireMediaEvents();
			this._lastFrameRateTime = new Date();
			this._fpsMonitor = (setInterval(this._checkFrameRate.bind(this), 1000, this._media));
			this._applyProperties(this._mediaOverrides);
		}
	}

	get paused() {
		return this._media.paused;
	}

	_getMediaProperty(prop) {
		//logger.log(`getMediaProperty prop: ${prop}, media: ${this._media}, ` +
		//	`mediaOverride:${this._mediaOverrides[prop]}, mediaDefault; ${this._mediaDefaults[prop]}`);

		if (this._media) {
			//console.log(`returning media property: ${prop}: ${this._media[prop]}`);
			return this._media[prop];
		}
		else if (this._mediaOverrides[prop] !== undefined) {
			return this._mediaOverrides[prop];
		}
		else {
			if (this._mediaDefaults[prop] === undefined) {
				throw new Error(`no default for media.${prop} defined`);
			}
			return this._mediaDefaults[prop];
		}
	}

	_setMediaProperty(prop, value) {
		if (this._mediaDefaults[prop] === undefined) {
			throw new Error(`media.${prop} has no default. Not safe to override with ${value}`);
		}

		// TODO: we might want to consider deleting properties which are getitng set to 'undefined'
		if (value === undefined) {
			throw new Error(`setting media.${prop} to undefined is not currently supported.`);
		}

		this._mediaOverrides[prop] = value;
		//console.log(`setting media override property: ${prop}: ${this._mediaOverrides[prop]}`);
		//console.log(Object.keys(this._mediaOverrides));

		if (this._media) {
			//console.log(`setting media property: ${prop}: ${this._media[prop]}`);
			this._media[prop] = value;
		}
	}

	_checkFrameRate() {
		let frames = 0;
		let time = new Date();
		let timeSinceLast = time.getTime() - this._lastFrameRateTime.getTime();

		if (this._media) {
			if (typeof this._media.getVideoPlaybackQuality === 'function') {
				frames = this._media.getVideoPlaybackQuality().totalVideoFrames;
			}
			else {
				frames = this._media.webkitDecodedFrameCount;
			}

			this._frameRate = Math.max((frames - this._lastDecoded), 0) * (1000 / timeSinceLast);
			this._lastDecoded = frames;

			if (this._safariFrameRateWorkaround) {
				let lastFrameRate = this._lastFrameRate;
				this._lastFrameRate = this._frameRate;

				if(this._frameRate === 0 && lastFrameRate !== 0) {
					this._frameRate = lastFrameRate;
				}
			}
		}
		else {
			this._lastDecoded = 0;
		}

		this._lastFrameRateTime = time;
	}

	get ended() {
		return this._getMediaProperty('ended');
	}

	get duration() {
		let duration = 0;

		if(this._media) {
			duration = this._media.duration;

			if(isNaN(duration)) {
				duration = 0;
			}
		}

		return duration;
	}

	get readyState() {
		return this._getMediaProperty('readyState');
	}

	get muted() {
		if (this._getMediaProperty('volume') === 0.0) {
			return true;
		}
		else {
			return this._getMediaProperty('muted');
		}
	}

	set muted(value) {
		//TODO: sanity check (true, false and undefined?)
		this._setMediaProperty('muted', !!value);
	}

	get volume() {
		// FIXME: this should return 0.0 if _muted
		return this._getMediaProperty('muted') ? 0.0 : this._getMediaProperty('volume');
	}

	set volume(value) {
		Utils.checkNotFiniteAndThrow(value, 'volume');
		Utils.checkLessThanZeroPointZeroAndThrow(value, 'volume');
		Utils.checkMoreThanOnePointZeroAndThrow(value, 'volume');

		this._setMediaProperty('volume', value);
	}

	get currentTime() {
		let time = this._pendingCurrentTime;

		if (time === undefined) {
			time = this._getMediaProperty('currentTime');
		}

		return time;
	}

	set currentTime(seconds) {
		// TODO: sanity check?
		if(seconds === this.currentTime) {
			//seeking to the current time causes Safari to stall, and there's no reason to do it in other browsers
			//emit a MEDIA_SEEKED, in case something is waiting on confirmation that it's done
			logger.log(`already at the seek time, faking a seek`);
			//this._emit(Event.MEDIA_SEEKING);
			this._emit(Event.MEDIA_SEEKED, { currentTime : this.currentTime });
		}
		else{
			if (this._readyForCurrentTime()) {
				this._media.currentTime = seconds;
			}
			else {
				logger.log(`Media not ready. Pending seek`);
				this._pendingCurrentTime = seconds;
			}
		}
	}

	get playbackRate() {
		if(this._pendingPlaybackRate !== undefined) {
			return this._pendingPlaybackRate;
		}
		else {
			return this._getMediaProperty('playbackRate');
		}
	}

	set playbackRate(value) {
		Utils.checkNotFiniteAndThrow(value, 'playbackRate');
		Utils.checkEqualToZeroPointZeroAndThrow(value, 'playbackRate');
		Utils.checkLessThanZeroPointZeroAndThrow(value, 'playbackRate');

		let newPlaybackRate = value;

		if (newPlaybackRate > 8) {
			logger.warn(`set playbackRate: fast forward is currently limited to 8x`);
			newPlaybackRate = 8;
		}

		if((this._usePendingPlaybackRate && this.currentTime === 0) || this._pendingPlaybackRate !== undefined) {
			logger.log('set playbackRate: Pending change to ' + newPlaybackRate);
			this._pendingPlaybackRate = newPlaybackRate;
		}
		else if (this.playbackRate !== newPlaybackRate) {
			logger.log('set playbackRate: Requesting change to ' + newPlaybackRate);
			this._expectedPlaybackRate = newPlaybackRate;
			this._rateChangeExpected = true;
			this._setMediaProperty('playbackRate', newPlaybackRate);
		}
		else if (this.playbackRate === newPlaybackRate && this._pendingPlaybackRate === undefined) {
			logger.log('set playbackRate: Rate is already ' + newPlaybackRate);

			this._emit(Event.MEDIA_RATECHANGE);
		}
	}

	get droppedFramesCount() {
		if (this._media) {
			if (typeof this._media.getVideoPlaybackQuality === 'function') {
				return this._media.getVideoPlaybackQuality().droppedVideoFrames;
			}
			else {
				return this._media.webkitDroppedFrameCount;
			}
		}
		else {
			return 0;
		}
	}

	get frameRate() {
		return this._frameRate;
	}

	get isPlaying() {
		let playing = false;

		if (this._media) {
			//current time is too slow to work at start.
			if (/*this._media.currentTime > 0 && */!this._media.paused && !this._media.ended && this._media.readyState > 2) {
				playing = true;
			}
		}

		return playing;
	}

	captionsControl() {
		//force the text track to hidden, then switch it to showing if we want it to be visible
		//running the caption control on canplaythrough fixes two issues:
		// - Firefox does not re-render captions after deleting cues. hiding then showing again forces a re-render.
		// - Tizen 2.3 switches text tracks to disabled on canplaythrough. we don't want the track in that mode, especially as we have to create the text track
		//   on startup or it won't work at all.
		if (this._media && this._media.textTracks && this._media.textTracks.length !== 0 ) {
			this._media.textTracks[0].mode = 'hidden';

			if (this._subtitlesEnabled) {
				this._media.textTracks[0].mode = 'showing';
			}
/*
			for (let i = 0; i < this._media.textTracks.length; i++) {
				this._media.textTracks[i].mode = newMode;
			}
*/
		}
	}

	get subtitlesEnabled() {
		return this._subtitlesEnabled;
	}

	set subtitlesEnabled(value) {
		this._subtitlesEnabled = !!value;
		this.captionsControl();
	}

	_applyProperties(propertyList) {
		if (this._media && propertyList) {

			let setProps = Object.keys(propertyList);

			//console.log(`_applyProperties: ${setProps} ${setProps.length}`);
			for (let i = 0; i < setProps.length; i++) {
				//console.log(`_applyProperties: before ${this._media[setProps[i]]}`);
				//console.log(`_applyProperties: ${setProps[i]}: ${propertyList[setProps[i]]}`);
				this._media[setProps[i]] = propertyList[setProps[i]];
				//console.log(`_applyProperties: after ${this._media[setProps[i]]}`);
			}
		}
	}

	_readMediaProperties() {
		let propertyList = {};

		if (this._media) {
			//walking the list would seem nicer but really all we want is..
			propertyList.muted = this._media.muted;
			propertyList.volume = this._media.volume;
			propertyList.playbackRate = this._media.playbackRate;
		}

		return propertyList;
	}

	_readyForCurrentTime() {
		return this._media && this._media.readyState >= minReadyStateForCurrentTime;
	}

}

export default MediaController;



// WEBPACK FOOTER //
// ./src/MediaController.js